import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ClubStateDispatcher } from '../state/club/club.dispatcher';
import { applyActionCode, authState, getAuth } from '@angular/fire/auth';
import { NavController } from '@ionic/angular';
import { AuthStoreDispatcher } from '../state/auth/auth.dispatcher';
import { firstValueFrom } from 'rxjs';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { SopoConfirmSettings } from 'src/app/sopo-popup/interfaces/confirm';
import { InviteCodesCreatePayload, RewardType } from '@soposopo/sopo-models';
import { ApiService } from '../backend/api.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicLinkService {

    constructor (
        private router: Router,
        private clubDispatcher: ClubStateDispatcher,
        private navCtrl: NavController,
        private authStoreDispatcher: AuthStoreDispatcher,
        private sopoPopupService: SopoPopupService,
        private apiService: ApiService,
    ) { }

    async createInviteBonusLink(userId: string): Promise<string> {
        let id = '';
        const rewardType = RewardType.EXPERIENCE_POINTS;
        const payload: InviteCodesCreatePayload = {
            userId,
            rewardType
        };

        try {
            const response = await this.apiService.saveInviteCode(payload);
            id = response.docId;
            if(id === '') throw Error('The id is an empty string');
        }
        catch (error) {
            console.error('[createInviteBonusLink - Error]', error);
            return '';
        }

        const url = new URL('https://link.playsopo.com/');
        url.searchParams.set('inviteCode', id);

        if (environment.staging)  url.searchParams.set('env', 'staging');

        return url.toString();
    }

    async createClubInviteLink(): Promise<string> {
        const clubId = await this.clubDispatcher.getClubID();
        const shortId = await this.clubDispatcher.getClubCode();

        const url = new URL('https://link.playsopo.com/lobby/home');
        url.searchParams.set('clubId', clubId);
        url.searchParams.set('shortId', shortId);

        if (environment.staging) url.searchParams.set('env', 'staging');

        return url.toString();
    }

    extractUrlParameters(url: string):  Record<string, string> {
        const params: Record<string, string> = {};

        const queryStart = url.indexOf('?');
        if (queryStart === -1) {
            return params;
        }

        const queryString = url.substring(queryStart + 1);

        const paramStrings = queryString.split('&');

        for (const paramString of paramStrings) {
            const [key, value] = paramString.split('=');
            params[key] = decodeURIComponent(value);
        }
        return params;
    }

    async handlePasswordResetAction(oobCode: string) {
        if (oobCode) {
            const user = await firstValueFrom(authState(getAuth()));
            if (user) {
                const options: SopoConfirmSettings = {
                    data: {
                        body: `You are signed in with another account. \nPlease sign out.`,
                        okText: 'Log out',
                        cancelText: 'Cancel'
                    }
                }
                const answer = await this.sopoPopupService.promptForConfirmation('app', options);
                if (answer) {
                    this.authStoreDispatcher.logout();
                    const navigationExtras: NavigationExtras = { queryParams: { oobCode }};
                    this.navCtrl.navigateForward('/password-change', navigationExtras);
                } else {
                    this.router.navigate([], {});
                }
            } else {
                const navigationExtras: NavigationExtras = { queryParams: { oobCode }};
                this.navCtrl.navigateForward('/password-change', navigationExtras);
            }
        }
    }

    async handleVerifyEmailAction(oobCode: string) {
        try {
            await applyActionCode(getAuth(), oobCode);
            const currentUser = getAuth().currentUser;
            if (currentUser) {
                currentUser.reload();
            }
            this.sopoPopupService.showAlert('app', {
                header: 'Verification Success',
                message: 'You have successfully verified your email address.',
                dismissAfter: 5000,
            });
        } catch (error) {
            this.sopoPopupService.showAlert('app', {
                header: 'Verification Error',
                message: 'Can\'t verify your email address, please try again later.',
                dismissAfter: 5000,
            });
        }
        this.router.navigate([], {});
    }

    async handleInviteLink(inviteCode: string) {
        if (inviteCode) {
            const user = await firstValueFrom(authState(getAuth()));
            if (user) {
                const options: SopoConfirmSettings = {
                    data: {
                        body: `You are signed in with another account! \nPlease sign out.`,
                        okText: 'Log out',
                        cancelText: 'Cancel'
                    }
                }
                const answer = await this.sopoPopupService.promptForConfirmation('app', options);
                if (answer) {
                    this.authStoreDispatcher.logout(false, inviteCode);
                } else {
                    this.router.navigate([], {});
                }
            }
        }
    }
}
